interface FAQItemProps {
  question: string;
  answer: string | JSX.Element;
}

interface FAQs {
  [key: string]: FAQItemProps[];
}

export const faqs: FAQs = {
  fq: [
    {
      question: "Comment créer un compte sur EventToBee ? ",
      answer: (
        <>
          <p className="mb-4">Pour créer votre compte EventToBee, suivez ces étapes simples :</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>
              <strong>Accédez à la page d'inscription :</strong> Cliquez sur "Compte" puis "S'inscrire" depuis la
              page d'accueil
            </li>
            <li>
              <strong>Choisissez votre profil :</strong> Sélectionnez votre rôle (Client, Hôte, ou Prestataire)
            </li>
            <li>
              <strong>Remplissez vos informations :</strong> Saisissez votre nom, email, et créez un mot de passe
              sécurisé
            </li>
            <li>
              <strong>Validez votre email :</strong> Vérifiez votre boîte mail et cliquez sur le lien de
              confirmation
            </li>
            <li>
              <strong>Complétez votre profil :</strong> Ajoutez vos informations personnelles pour une expérience
              optimale
            </li>
          </ol>
          <p className="mt-4 text-sm text-gray-600">
            💡 Astuce : Utilisez un mot de passe fort avec au moins 8 caractères, incluant majuscules, minuscules
            et chiffres.
          </p>
        </>
      ),
    },
    {
      question: "Comment se connecter à votre compte ? ",
      answer: (
        <>
          <p className="mb-4">Pour vous connecter à votre compte EventToBee :</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>
              <strong>Cliquez sur "Compte" :</strong> Depuis la page d'accueil, sélectionnez "Se connecter"
            </li>
            <li>
              <strong>Saisissez vos identifiants :</strong> Entrez votre adresse email et mot de passe
            </li>
            <li>
              <strong>Connectez-vous :</strong> Cliquez sur "Se connecter" pour accéder à votre espace personnel
            </li>
          </ol>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm">
              <strong>Mot de passe oublié ?</strong> Cliquez sur "Mot de passe oublié" et suivez les instructions
              par email pour le réinitialiser.
            </p>
          </div>
        </>
      ),
    },
    {
      question: "Comment contacter le support client en cas de problème ? ",
      answer: (
        <>
          <p className="mb-4">
            Notre équipe support est là pour vous aider ! Plusieurs moyens de nous contacter :
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📧</span>
              <div>
                <p>
                  <strong>Email :</strong>{" "}
                  <a href="mailto:support@eventtobee.com" className="text-blue-600 hover:underline">
                    support@eventtobee.com
                  </a>
                </p>
                <p className="text-sm text-gray-600">Réponse sous 24h en moyenne</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📞</span>
              <div>
                <p>
                  <strong>Téléphone :</strong> 09 77 93 30 63
                </p>
                <p className="text-sm text-gray-600">Lun-Ven : 9h-18h (appel non surtaxé)</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">💬</span>
              <div>
                <p>
                  <strong>Chat en ligne :</strong> Disponible depuis votre espace client
                </p>
                <p className="text-sm text-gray-600">Réponse immédiate aux heures ouvrables</p>
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">
            💡 Pour un traitement plus rapide, précisez votre numéro de réservation et décrivez votre problème en
            détail.
          </p>
        </>
      ),
    },
    {
      question: "Comment mettre à jour votre profil ? ",
      answer: (
        <>
          <p className="mb-4">Pour modifier vos informations personnelles :</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>
              <strong>Connectez-vous :</strong> Accédez à votre compte EventToBee
            </li>
            <li>
              <strong>Allez dans "Mon Profil" :</strong> Cliquez sur votre nom en haut à droite, puis "Mon Profil"
            </li>
            <li>
              <strong>Modifiez vos informations :</strong> Changez vos données personnelles, photo de profil, etc.
            </li>
            <li>
              <strong>Sauvegardez :</strong> Cliquez sur "Enregistrer les modifications"
            </li>
          </ol>
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm">
              <strong>⚠️ Important :</strong> Certaines modifications (comme l'email) peuvent nécessiter une
              validation par email.
            </p>
          </div>
        </>
      ),
    },
    {
      question: "Comment modifier/réinitialiser votre mot de passe ?",
      answer: (
        <>
          <p className="mb-4">Pour changer votre mot de passe :</p>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">🔑 Si vous connaissez votre mot de passe actuel :</h4>
            <ol className="list-decimal list-inside space-y-1 ml-4">
              <li>Connectez-vous à votre compte</li>
              <li>Allez dans "Mon Profil" → "Sécurité"</li>
              <li>Cliquez sur "Modifier le mot de passe"</li>
              <li>Saisissez l'ancien et le nouveau mot de passe</li>
              <li>Confirmez les modifications</li>
            </ol>
          </div>

          <div>
            <h4 className="font-semibold mb-2">🔓 Si vous avez oublié votre mot de passe :</h4>
            <ol className="list-decimal list-inside space-y-1 ml-4">
              <li>Cliquez sur "Mot de passe oublié" sur la page de connexion</li>
              <li>Saisissez votre adresse email</li>
              <li>Vérifiez vos emails et cliquez sur le lien de réinitialisation</li>
              <li>Créez un nouveau mot de passe sécurisé</li>
            </ol>
          </div>

          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm">
              <strong>🛡️ Sécurité :</strong> Utilisez un mot de passe unique avec au moins 8 caractères, incluant
              majuscules, minuscules, chiffres et caractères spéciaux.
            </p>
          </div>
        </>
      ),
    },
  ],
  utilisateurs: [
    {
      question: "Comment réserver un espace ?",
      answer: (
        <>
          <p className="mb-4">Réserver un espace sur EventToBee est simple et rapide :</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>
              <strong>Recherchez votre espace :</strong> Utilisez les filtres (localisation, type d'événement,
              capacité, prix) pour trouver l'espace parfait
            </li>
            <li>
              <strong>Consultez les détails :</strong> Vérifiez les photos, équipements disponibles, règles de
              l'espace et avis clients
            </li>
            <li>
              <strong>Vérifiez la disponibilité :</strong> Sélectionnez vos dates et horaires souhaités sur le
              calendrier
            </li>
            <li>
              <strong>Ajoutez au panier :</strong> Choisissez vos options supplémentaires si disponibles
            </li>
            <li>
              <strong>Finalisez la réservation :</strong> Remplissez vos informations et procédez au paiement
              sécurisé
            </li>
            <li>
              <strong>Confirmation :</strong> Recevez votre confirmation par email avec tous les détails
            </li>
          </ol>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm">
              <strong>💡 Astuce :</strong> Ajoutez l'espace à vos favoris pour le retrouver facilement plus tard !
            </p>
          </div>
        </>
      ),
    },
    {
      question: "Comment laisser un avis sur un espace que j'ai réservé ?",
      answer: (
        <>
          <p className="mb-4">Votre avis aide notre communauté ! Voici comment procéder :</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>
              <strong>Connectez-vous :</strong> Accédez à votre compte EventToBee
            </li>
            <li>
              <strong>Allez dans "Mes réservations" :</strong> Trouvez la réservation terminée
            </li>
            <li>
              <strong>Cliquez sur "Laisser un avis" :</strong> Le bouton apparaît après la fin de votre événement
            </li>
            <li>
              <strong>Notez l'espace :</strong> Donnez une note de 1 à 5 étoiles
            </li>
            <li>
              <strong>Rédigez votre commentaire :</strong> Partagez votre expérience de manière constructive
            </li>
            <li>
              <strong>Publiez :</strong> Votre avis sera visible après validation
            </li>
          </ol>
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm">
              <strong>🌟 Merci !</strong> Vos avis aident les autres utilisateurs à faire leur choix et les hôtes à
              s'améliorer.
            </p>
          </div>
        </>
      ),
    },
    {
      question: "Combien de temps le responsable du lieu a-t-il pour répondre à ma demande de réservation ?",
      answer: (
        <>
          <p className="mb-4">
            Les hôtes ont <strong>48 heures maximum</strong> pour répondre à votre demande de réservation.
          </p>
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">⏰ Délais de réponse :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Réponse standard :</strong> Sous 48h maximum
                </li>
                <li>
                  <strong>Réponse rapide :</strong> Beaucoup d'hôtes répondent sous 24h
                </li>
                <li>
                  <strong>Réponse immédiate :</strong> Certains espaces ont la réservation instantanée
                </li>
              </ul>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📱 Notifications :</h4>
              <p className="text-sm">
                Vous recevrez une notification par email et/ou SMS dès que l'hôte répond à votre demande.
              </p>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">
            💡 En cas de non-réponse dans les délais, votre demande est automatiquement annulée et vous êtes
            remboursé intégralement.
          </p>
        </>
      ),
    },
    {
      question: "Que faire si l'hôte ne répond pas dans les 48 heures ?",
      answer: (
        <>
          <p className="mb-4">Si un hôte ne répond pas dans les 48h, voici ce qui se passe automatiquement :</p>

          <div className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">🚨 Annulation automatique :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Votre demande est automatiquement annulée</li>
                <li>
                  Vous êtes remboursé <strong>intégralement</strong> et immédiatement
                </li>
                <li>Vous recevez un email de confirmation d'annulation</li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💡 Que pouvez-vous faire ?</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Rechercher un autre espace similaire</li>
                <li>
                  Contacter notre support pour obtenir de l'aide :{" "}
                  <a href="mailto:support@eventtobee.com" className="text-blue-600 hover:underline">
                    support@eventtobee.com
                  </a>
                </li>
                <li>Utiliser nos filtres pour trouver des espaces avec réservation instantanée</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">🛡️ Protection client :</h4>
              <p className="text-sm">
                EventToBee s'engage à protéger vos intérêts. Les hôtes récidivistes sont sanctionnés pour garantir
                une expérience de qualité.
              </p>
            </div>
          </div>
        </>
      ),
    },
  ],
  hôte: [
    {
      question: "Comment ajouter un espace ?",
      answer: (
        <>
          <p className="mb-4">Pour mettre votre espace en location sur EventToBee :</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>
              <strong>Créez votre compte hôte :</strong> Inscrivez-vous en tant que propriétaire d'espace
            </li>
            <li>
              <strong>Accédez à "Mes espaces" :</strong> Dans votre tableau de bord, cliquez sur "Ajouter un
              espace"
            </li>
            <li>
              <strong>Remplissez les informations :</strong> Titre, description détaillée, type d'événements
              acceptés
            </li>
            <li>
              <strong>Ajoutez des photos de qualité :</strong> Minimum 5 photos haute résolution de votre espace
            </li>
            <li>
              <strong>Précisez les équipements :</strong> Listez tous les équipements inclus (tables, chaises,
              sono, etc.)
            </li>
            <li>
              <strong>Définissez vos tarifs :</strong> Prix par heure/journée, caution, frais de ménage
            </li>
            <li>
              <strong>Configurez la disponibilité :</strong> Définissez vos créneaux disponibles
            </li>
            <li>
              <strong>Validation :</strong> Notre équipe vérifie votre annonce sous 48h
            </li>
          </ol>
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm">
              <strong>🏆 Conseil :</strong> Plus votre annonce est complète et attractive, plus vous aurez de
              réservations !
            </p>
          </div>
        </>
      ),
    },
    {
      question: "Comment gérer les disponibilités de mes espaces ?",
      answer: (
        <>
          <p className="mb-4">La gestion de vos disponibilités est cruciale pour optimiser vos réservations :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📅 Calendrier de disponibilité :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Accédez à "Mes espaces" → Sélectionnez votre espace → "Calendrier"</li>
                <li>Cliquez sur les dates pour les rendre disponibles/indisponibles</li>
                <li>Définissez des créneaux horaires précis pour chaque jour</li>
                <li>Bloquez les dates d'entretien ou d'usage personnel</li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">⚡ Mise à jour en temps réel :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Vos modifications sont visibles immédiatement</li>
                <li>Les clients voient la disponibilité en direct</li>
                <li>Possibilité de programmer des changements à l'avance</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">💡 Bonnes pratiques :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Mettez à jour régulièrement votre calendrier</li>
                <li>Prévoyez du temps entre les réservations pour le nettoyage</li>
                <li>Activez les notifications pour être alerté des nouvelles demandes</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Suis-je notifié des demandes de réservation ? ",
      answer: (
        <>
          <p className="mb-4">Oui, vous êtes alerté instantanément de toutes les demandes :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📱 Notifications immédiates :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Email :</strong> Notification détaillée avec toutes les informations
                </li>
                <li>
                  <strong>SMS :</strong> Alerte rapide (si activée dans vos préférences)
                </li>
                <li>
                  <strong>Application mobile :</strong> Push notification en temps réel
                </li>
                <li>
                  <strong>Tableau de bord :</strong> Badge de notification sur votre compte
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">⏰ Délai de réponse :</h4>
              <p className="text-sm">
                Vous avez <strong>48 heures maximum</strong> pour accepter ou refuser une demande. Au-delà, la
                demande est automatiquement annulée.
              </p>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">⚙️ Personnalisation :</h4>
              <p className="text-sm">
                Configurez vos préférences de notification dans "Mon Profil" → "Notifications" pour choisir comment
                être contacté.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Comment puis-je mettre à jour et bloquer la disponibilité de mon espace sur le calendrier ?",
      answer: (
        <>
          <p className="mb-4">Gérez facilement votre calendrier en quelques clics :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📅 Mettre à jour la disponibilité :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Allez dans "Mes espaces" → Sélectionnez votre espace</li>
                <li>Cliquez sur "Calendrier" ou "Disponibilités"</li>
                <li>Cliquez sur une date pour la rendre disponible (vert) ou indisponible (rouge)</li>
                <li>Définissez les créneaux horaires spécifiques si nécessaire</li>
                <li>Sauvegardez vos modifications</li>
              </ol>
            </div>

            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">🚫 Bloquer des dates :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Usage personnel :</strong> Bloquez quand vous utilisez votre espace
                </li>
                <li>
                  <strong>Maintenance :</strong> Période d'entretien ou rénovation
                </li>
                <li>
                  <strong>Vacances :</strong> Absence prolongée
                </li>
                <li>
                  <strong>Événements privés :</strong> Réservations hors plateforme
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">💡 Fonctionnalités avancées :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Récurrence :</strong> Bloquez automatiquement certains jours (ex: tous les lundis)
                </li>
                <li>
                  <strong>Import calendrier :</strong> Synchronisez avec Google Calendar ou Outlook
                </li>
                <li>
                  <strong>Tarifs variables :</strong> Définissez des prix différents selon les périodes
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Comment puis-je mettre à jour mon espace ?",
      answer: (
        <>
          <p className="mb-4">Maintenez votre annonce à jour pour maximiser vos réservations :</p>

          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>
              <strong>Accédez à "Mes espaces" :</strong> Depuis votre tableau de bord
            </li>
            <li>
              <strong>Sélectionnez l'espace à modifier :</strong> Cliquez sur "Modifier" ou l'icône crayon
            </li>
            <li>
              <strong>Mettez à jour les informations :</strong> Description, équipements, règles, photos
            </li>
            <li>
              <strong>Ajustez vos tarifs :</strong> Modifiez vos prix selon la demande
            </li>
            <li>
              <strong>Actualisez les photos :</strong> Ajoutez de nouvelles images si vous avez rénové
            </li>
            <li>
              <strong>Sauvegardez :</strong> Cliquez sur "Enregistrer les modifications"
            </li>
          </ol>

          <div className="mt-4 space-y-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm">
                <strong>📸 Photos :</strong> Changez régulièrement vos photos pour montrer les améliorations et
                variations saisonnières.
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <p className="text-sm">
                <strong>⚠️ Validation :</strong> Les modifications importantes peuvent nécessiter une re-validation
                par notre équipe.
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm">
                <strong>💡 Optimisation :</strong> Analysez vos statistiques pour voir quels changements augmentent
                vos réservations.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Que dois-je faire si un utilisateur reste au-delà de l'heure de fin de sa réservation ?",
      answer: (
        <>
          <p className="mb-4">Si un client dépasse l'heure de fin de réservation :</p>

          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">⚠️ Première approche :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Contactez directement le client par téléphone ou message</li>
                <li>Rappelez poliment l'heure de fin de réservation</li>
                <li>Laissez un délai de grâce de 15-30 minutes si possible</li>
                <li>Documentez les échanges (captures d'écran)</li>
              </ol>
            </div>

            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">🚨 Si le problème persiste :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Contactez notre support :</strong> 09 77 93 30 63 ou{" "}
                  <a href="mailto:support@eventtobee.com" className="text-blue-600 hover:underline">
                    support@eventtobee.com
                  </a>
                </li>
                <li>
                  <strong>Signalement immédiat :</strong> Notre équipe intervient rapidement
                </li>
                <li>
                  <strong>Compensation :</strong> Frais supplémentaires facturés au client
                </li>
                <li>
                  <strong>Protection :</strong> Assistance juridique si nécessaire
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">💰 Compensation automatique :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Facturation automatique du dépassement au tarif horaire majoré</li>
                <li>Utilisation de la caution si nécessaire</li>
                <li>Dédommagement pour les désagréments causés</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Pourquoi EventToBee a besoin de mes informations bancaires ?",
      answer: (
        <>
          <p className="mb-4">
            Vos informations bancaires sont essentielles pour plusieurs raisons sécuritaires et légales :
          </p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💳 Paiements automatiques :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Virements directs :</strong> Réception automatique de vos revenus locatifs
                </li>
                <li>
                  <strong>Sécurité :</strong> Transactions cryptées via notre partenaire Stripe
                </li>
                <li>
                  <strong>Rapidité :</strong> Paiement sous 48h après la fin de l'événement
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">🛡️ Sécurité et conformité :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Vérification d'identité :</strong> Prévention de la fraude
                </li>
                <li>
                  <strong>Conformité légale :</strong> Respect des réglementations bancaires
                </li>
                <li>
                  <strong>Traçabilité :</strong> Suivi transparent de toutes les transactions
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">🔒 Protection des données :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Cryptage bancaire :</strong> Normes de sécurité les plus strictes
                </li>
                <li>
                  <strong>Partenaire certifié :</strong> Stripe, leader mondial des paiements
                </li>
                <li>
                  <strong>Conformité RGPD :</strong> Vos données sont protégées et jamais revendues
                </li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">📊 Avantages pour vous :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Paiements automatiques sans intervention</li>
                <li>Suivi détaillé de vos revenus dans votre tableau de bord</li>
                <li>Déclarations fiscales simplifiées avec nos rapports</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ],
  paiement: [
    {
      question: "Quelle est la commission EventToBee ?",
      answer: (
        <>
          <p className="mb-4">Nos commissions sont transparentes et compétitives :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💰 Structure des commissions :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Commission hôte :</strong> 3% du montant de la réservation
                </li>
                <li>
                  <strong>Frais de service client :</strong> 6-12% selon le montant de la réservation
                </li>
                <li>
                  <strong>Frais de paiement :</strong> 2.9% + 0,30€ (frais Stripe inclus)
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Ce qui est inclus :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Plateforme de réservation sécurisée</li>
                <li>Support client 7j/7</li>
                <li>Assurance et protection des paiements</li>
                <li>Outils de gestion et statistiques</li>
                <li>Marketing et visibilité de votre espace</li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📊 Exemple concret :</h4>
              <p className="text-sm">Pour une réservation de 100€ :</p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                <li>Client paie : 108€ (100€ + 8€ de frais de service)</li>
                <li>Hôte reçoit : 97€ (100€ - 3€ de commission)</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Comment puis-je obtenir un reçu pour mon paiement ?",
      answer: (
        <>
          <p className="mb-4">Vos reçus et factures sont automatiquement générés et facilement accessibles :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📧 Envoi automatique :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Reçu par email immédiatement après le paiement</li>
                <li>Facture détaillée disponible dans votre espace client</li>
                <li>Récapitulatif mensuel de toutes vos transactions</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">💻 Accès en ligne :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Connectez-vous à votre compte EventToBee</li>
                <li>Allez dans "Mes réservations" ou "Mes transactions"</li>
                <li>Cliquez sur "Télécharger la facture" ou "Voir le reçu"</li>
                <li>Format PDF prêt pour vos démarches administratives</li>
              </ol>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📄 Informations incluses :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Détails de la réservation (dates, lieu, durée)</li>
                <li>Montants HT et TTC</li>
                <li>Numéro de facture unique</li>
                <li>Informations légales EventToBee</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">🏢 Pour les entreprises :</h4>
              <p className="text-sm">
                Factures conformes avec TVA déductible, parfaites pour vos notes de frais et comptabilité.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Quel est le mode de paiement en ligne ?",
      answer: (
        <>
          <p className="mb-4">
            EventToBee propose plusieurs moyens de paiement sécurisés via notre partenaire Stripe :
          </p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💳 Cartes bancaires :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Cartes de crédit :</strong> Visa, Mastercard, American Express
                </li>
                <li>
                  <strong>Cartes de débit :</strong> Cartes bancaires françaises et européennes
                </li>
                <li>
                  <strong>Paiement 3D Secure :</strong> Protection renforcée contre la fraude
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">📱 Paiements numériques :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Apple Pay :</strong> Paiement rapide depuis iPhone/iPad
                </li>
                <li>
                  <strong>Google Pay :</strong> Paiement sécurisé depuis Android
                </li>
                <li>
                  <strong>PayPal :</strong> Utilisez votre compte PayPal existant
                </li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">🏦 Virement bancaire :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Disponible pour les réservations importantes</li>
                <li>Délai de traitement : 1-3 jours ouvrés</li>
                <li>Parfait pour les événements d'entreprise</li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">🛡️ Sécurité maximale :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Cryptage SSL :</strong> Toutes vos données sont protégées
                </li>
                <li>
                  <strong>Conformité PCI DSS :</strong> Standards bancaires les plus stricts
                </li>
                <li>
                  <strong>Protection anti-fraude :</strong> Détection automatique des transactions suspectes
                </li>
                <li>
                  <strong>Garantie remboursement :</strong> Protection totale en cas de problème
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm">
              <strong>💡 Astuce :</strong> Sauvegardez vos moyens de paiement pour des réservations encore plus
              rapides !
            </p>
          </div>
        </>
      ),
    },
    {
      question: "Quand et comment suis-je remboursé en cas d'annulation ?",
      answer: (
        <>
          <p className="mb-4">
            Les remboursements s'effectuent selon la politique d'annulation choisie par l'hôte :
          </p>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💰 Politiques de remboursement :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Flexible :</strong> Remboursement intégral jusqu'à 24h avant
                </li>
                <li>
                  <strong>Modérée :</strong> Remboursement intégral jusqu'à 5 jours avant
                </li>
                <li>
                  <strong>Stricte :</strong> Remboursement intégral jusqu'à 14 jours avant
                </li>
                <li>
                  <strong>Très stricte :</strong> Remboursement 50% jusqu'à 30 jours avant
                </li>
              </ul>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">⏰ Délais de remboursement :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Carte bancaire :</strong> 5-10 jours ouvrés
                </li>
                <li>
                  <strong>PayPal :</strong> 2-3 jours ouvrés
                </li>
                <li>
                  <strong>Virement bancaire :</strong> 3-5 jours ouvrés
                </li>
                <li>
                  <strong>Annulation hôte :</strong> Remboursement immédiat + compensation
                </li>
              </ul>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">🛡️ Garanties EventToBee :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Remboursement automatique si l'hôte annule</li>
                <li>Protection contre les fraudes</li>
                <li>Assistance 24/7 pour vos demandes de remboursement</li>
                <li>Médiation gratuite en cas de litige</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Puis-je modifier le mode de paiement après avoir effectué une réservation ?",
      answer: (
        <>
          <p className="mb-4">La modification du mode de paiement dépend du statut de votre réservation :</p>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Possible dans ces cas :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Réservation en attente :</strong> Avant confirmation de l'hôte
                </li>
                <li>
                  <strong>Paiement échoué :</strong> Carte expirée ou refusée
                </li>
                <li>
                  <strong>Paiement en plusieurs fois :</strong> Pour les échéances futures
                </li>
                <li>
                  <strong>Demande d'avenant :</strong> Avec accord de l'hôte
                </li>
              </ul>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">❌ Impossible après :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Confirmation et encaissement réussi</li>
                <li>Début de l'événement</li>
                <li>Remboursement déjà traité</li>
              </ul>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">🔄 Comment procéder :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Contactez notre support au 09 77 93 30 63</li>
                <li>Fournissez votre numéro de réservation</li>
                <li>Précisez le nouveau mode de paiement souhaité</li>
                <li>Suivez les instructions pour finaliser le changement</li>
              </ol>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Y a-t-il des frais cachés ou supplémentaires ?",
      answer: (
        <>
          <p className="mb-4">EventToBee pratique une politique de transparence totale sur les prix :</p>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Transparence garantie :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Prix affiché :</strong> Prix final TTC visible avant paiement
                </li>
                <li>
                  <strong>Frais détaillés :</strong> Décomposition claire de tous les coûts
                </li>
                <li>
                  <strong>Pas de surprises :</strong> Aucun frais caché à la réservation
                </li>
                <li>
                  <strong>Récapitulatif :</strong> Confirmation détaillée avant validation
                </li>
              </ul>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💰 Composition du prix :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Prix de l'espace (fixé par l'hôte)</li>
                <li>Frais de service EventToBee (6-12%)</li>
                <li>Frais de traitement du paiement (2.9% + 0,30€)</li>
                <li>TVA sur les frais de service (20%)</li>
                <li>Caution éventuelle (remboursable)</li>
              </ul>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">⚠️ Frais optionnels possibles :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Services additionnels :</strong> Choisis par vous (ménage, matériel extra)
                </li>
                <li>
                  <strong>Dépassement horaire :</strong> Si vous restez plus longtemps
                </li>
                <li>
                  <strong>Frais de modification :</strong> Si vous changez votre réservation
                </li>
                <li>
                  <strong>Frais d'annulation :</strong> Selon la politique de l'hôte
                </li>
              </ul>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">🔍 Vérification facile :</h4>
              <p className="text-sm">
                Avant chaque paiement, un récapitulatif détaillé vous présente tous les frais. Aucune transaction
                ne peut être effectuée sans votre validation explicite.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Comment fonctionne le paiement échelonné pour les gros événements ?",
      answer: (
        <>
          <p className="mb-4">
            Pour les événements importants, EventToBee propose des solutions de paiement échelonné :
          </p>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💳 Conditions d'éligibilité :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Montant minimum :</strong> Réservations de plus de 500€
                </li>
                <li>
                  <strong>Délai :</strong> Événement prévu dans plus de 30 jours
                </li>
                <li>
                  <strong>Durée :</strong> Location de plus de 6 heures
                </li>
                <li>
                  <strong>Type :</strong> Événements professionnels, mariages, gros événements
                </li>
              </ul>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">📅 Options de paiement :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>2 fois :</strong> 50% à la réservation, 50% à J-15
                </li>
                <li>
                  <strong>3 fois :</strong> 40% à la réservation, 30% à J-30, 30% à J-15
                </li>
                <li>
                  <strong>4 fois :</strong> Pour les événements de plus de 2000€
                </li>
                <li>
                  <strong>Sur mesure :</strong> Plan personnalisé pour événements exceptionnels
                </li>
              </ul>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">🔄 Processus de demande :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Sélectionnez "Paiement échelonné" lors de la réservation</li>
                <li>Choisissez votre plan de paiement préféré</li>
                <li>Validation automatique ou manuelle selon le montant</li>
                <li>Confirmation par email avec échéancier détaillé</li>
                <li>Prélèvements automatiques aux dates convenues</li>
              </ol>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">🛡️ Sécurité et garanties :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Pas de frais supplémentaires pour l'échelonnement</li>
                <li>Modification possible jusqu'à J-30</li>
                <li>Annulation avec remboursement selon politique</li>
                <li>Support dédié pour les gros événements</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Que se passe-t-il si mon paiement est refusé ?",
      answer: (
        <>
          <p className="mb-4">En cas de paiement refusé, plusieurs solutions s'offrent à vous :</p>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">❌ Causes fréquentes de refus :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Solde insuffisant :</strong> Fonds manquants sur le compte
                </li>
                <li>
                  <strong>Carte expirée :</strong> Date de validité dépassée
                </li>
                <li>
                  <strong>Plafond atteint :</strong> Limite de paiement journalière/mensuelle
                </li>
                <li>
                  <strong>Sécurité bancaire :</strong> Transaction bloquée par votre banque
                </li>
                <li>
                  <strong>Données incorrectes :</strong> Informations de carte erronées
                </li>
              </ul>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">🔄 Actions immédiates :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>
                  <strong>Vérifiez vos informations :</strong> Numéro, date, cryptogramme
                </li>
                <li>
                  <strong>Contactez votre banque :</strong> Vérifiez les blocages et plafonds
                </li>
                <li>
                  <strong>Tentez un autre mode :</strong> Carte différente, PayPal, virement
                </li>
                <li>
                  <strong>Réessayez plus tard :</strong> Après correction du problème
                </li>
              </ol>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">🆘 Support EventToBee :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Chat en direct :</strong> Assistance immédiate sur le site
                </li>
                <li>
                  <strong>Téléphone :</strong> 09 77 93 30 63 (7j/7, 9h-21h)
                </li>
                <li>
                  <strong>Email :</strong> support@eventtobee.com
                </li>
                <li>
                  <strong>Solutions alternatives :</strong> Paiement manuel, virement, report
                </li>
              </ul>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">⏰ Délais de grâce :</h4>
              <p className="text-sm">
                <strong>24h :</strong> Délai automatique pour régulariser votre paiement.
                <br />
                <strong>48h :</strong> Extension possible sur demande pour événements urgents.
                <br />
                <strong>Protection :</strong> Votre réservation reste bloquée pendant ce délai.
              </p>
            </div>
          </div>
        </>
      ),
    },
  ],
  sécurité: [
    {
      question: "Comment fonctionne le dépôt de garantie indiqué par l'hôte et quand est-il restitué ?",
      answer: (
        <>
          <p className="mb-4">Le dépôt de garantie protège les hôtes contre les dommages éventuels :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💰 Fonctionnement du dépôt :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Montant variable :</strong> Défini par chaque hôte selon l'espace
                </li>
                <li>
                  <strong>Préautorisation :</strong> Blocage temporaire sur votre carte sans débit
                </li>
                <li>
                  <strong>Durée :</strong> Maintenu pendant toute la durée de l'événement + 48h
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Restitution automatique :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Sans dommage :</strong> Libération automatique sous 48h après l'événement
                </li>
                <li>
                  <strong>Avec dommages :</strong> Déduction des frais de réparation documentés
                </li>
                <li>
                  <strong>Contestation :</strong> Médiation gratuite par notre équipe
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📋 État des lieux :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Photos avant/après l'événement recommandées</li>
                <li>Signalement immédiat de tout problème</li>
                <li>Conservation des preuves (photos, témoignages)</li>
              </ul>
            </div>

            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">⚠️ Cas de retenue :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Dommages matériels prouvés</li>
                <li>Nettoyage exceptionnel nécessaire</li>
                <li>Non-respect des règles de l'espace</li>
                <li>Dépassement d'horaires non autorisé</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Quelle est la politique d'annulation et de remboursement de EventToBee ?",
      answer: (
        <>
          <p className="mb-4">Notre politique d'annulation est conçue pour être équitable pour tous :</p>

          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Annulation plus de 5 jours avant :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Remboursement :</strong> 70% du montant total de la réservation
                </li>
                <li>
                  <strong>Délai :</strong> Remboursement sous 5-10 jours ouvrés
                </li>
                <li>
                  <strong>Frais conservés :</strong> 30% pour couvrir les coûts de traitement
                </li>
              </ul>
            </div>

            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">❌ Annulation moins de 5 jours avant :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Aucun remboursement :</strong> Le montant total est conservé
                </li>
                <li>
                  <strong>Exception :</strong> Circonstances exceptionnelles étudiées au cas par cas
                </li>
                <li>
                  <strong>Force majeure :</strong> Remboursement intégral (confinement, catastrophe naturelle)
                </li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">🏥 Circonstances exceptionnelles :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Hospitalisation (certificat médical requis)</li>
                <li>Décès d'un proche (justificatif nécessaire)</li>
                <li>Cas de force majeure documentés</li>
                <li>Annulation par l'hôte (remboursement intégral + compensation)</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">📞 Comment annuler :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Connectez-vous à votre compte</li>
                <li>Allez dans "Mes réservations"</li>
                <li>Cliquez sur "Annuler la réservation"</li>
                <li>Confirmez par email pour finaliser</li>
              </ol>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Que se passe-t-il si un utilisateur ne se présente pas à sa réservation confirmée ?",
      answer: (
        <>
          <p className="mb-4">En cas de "no-show" (non-présentation) :</p>

          <div className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">🚫 Conséquences immédiates :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Aucun remboursement :</strong> Le montant total est conservé
                </li>
                <li>
                  <strong>Signalement :</strong> Note dans votre profil client
                </li>
                <li>
                  <strong>Paiement à l'hôte :</strong> Compensation intégrale versée
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">⏰ Délai de grâce :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>30 minutes :</strong> Délai accordé après l'heure de début
                </li>
                <li>
                  <strong>Contact obligatoire :</strong> Prévenez l'hôte en cas de retard
                </li>
                <li>
                  <strong>Justification :</strong> Circonstances exceptionnelles évaluées
                </li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📞 Que faire en cas d'empêchement :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Contactez immédiatement l'hôte</li>
                <li>Appelez notre support : 09 77 93 30 63</li>
                <li>Expliquez la situation (urgence médicale, transport, etc.)</li>
                <li>Fournissez des justificatifs si possible</li>
              </ol>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">💡 Pour éviter les no-shows :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Confirmez votre présence 24h avant</li>
                <li>Enregistrez les coordonnées de l'hôte</li>
                <li>Préparez votre itinéraire à l'avance</li>
                <li>Activez les notifications de rappel</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">⚖️ Récidive :</h4>
              <p className="text-sm">
                En cas de no-shows répétés, votre compte peut être suspendu temporairement pour protéger notre
                communauté d'hôtes.
              </p>
            </div>
          </div>
        </>
      ),
    },
  ],
  réservation: [
    {
      question: "Les utilisateurs voient-ils quand l'hôte accepte ou refuse une demande ?",
      answer: (
        <>
          <p className="mb-4">Oui, vous êtes informé en temps réel de toutes les réponses des hôtes :</p>

          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Réponse positive :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Notification immédiate :</strong> Email et SMS de confirmation
                </li>
                <li>
                  <strong>Détails complets :</strong> Informations de contact de l'hôte
                </li>
                <li>
                  <strong>Instructions d'accès :</strong> Code d'entrée, parking, consignes
                </li>
                <li>
                  <strong>Statut mis à jour :</strong> "Confirmé" dans votre tableau de bord
                </li>
              </ul>
            </div>

            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">❌ Réponse négative :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Notification de refus :</strong> Email explicatif de l'hôte
                </li>
                <li>
                  <strong>Remboursement automatique :</strong> Fonds libérés immédiatement
                </li>
                <li>
                  <strong>Suggestions alternatives :</strong> Espaces similaires proposés
                </li>
                <li>
                  <strong>Statut :</strong> "Refusé" avec possibilité de nouvelle recherche
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">⏱️ Pas de réponse :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Délai de 48h :</strong> Annulation automatique si pas de réponse
                </li>
                <li>
                  <strong>Remboursement intégral :</strong> Fonds restitués automatiquement
                </li>
                <li>
                  <strong>Notification :</strong> Information de la situation par email
                </li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📱 Moyens de notification :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Email détaillé avec toutes les informations</li>
                <li>SMS de confirmation rapide</li>
                <li>Notification push sur l'application mobile</li>
                <li>Mise à jour en temps réel dans votre espace client</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Qu'est-ce que la ligne « Frais de service » dans une demande de réservation ?",
      answer: (
        <>
          <p className="mb-4">Les frais de service couvrent le fonctionnement de la plateforme EventToBee :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💰 Composition des frais :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Pourcentage variable :</strong> 6% à 12% selon le montant de la réservation
                </li>
                <li>
                  <strong>Frais de paiement :</strong> 2.9% + 0,30€ (traitement bancaire sécurisé)
                </li>
                <li>
                  <strong>TVA :</strong> 20% sur les frais de service uniquement
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Services inclus :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Plateforme sécurisée :</strong> Réservation et paiement protégés
                </li>
                <li>
                  <strong>Support client :</strong> Assistance 7j/7 avant, pendant et après votre événement
                </li>
                <li>
                  <strong>Assurance :</strong> Protection contre les annulations de dernière minute
                </li>
                <li>
                  <strong>Médiation :</strong> Résolution gratuite des litiges
                </li>
                <li>
                  <strong>Outils de gestion :</strong> Suivi de vos réservations et factures
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📊 Exemple de calcul :</h4>
              <div className="text-sm space-y-1">
                <p>
                  <strong>Réservation de 200€ :</strong>
                </p>
                <ul className="list-disc list-inside ml-4">
                  <li>Prix de l'espace : 200€</li>
                  <li>Frais de service (8%) : 16€</li>
                  <li>Frais de paiement : 6,10€</li>
                  <li>
                    <strong>Total à payer : 222,10€</strong>
                  </li>
                </ul>
              </div>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">🔒 Transparence totale :</h4>
              <p className="text-sm">
                Tous les frais sont clairement affichés avant la validation de votre réservation. Aucun frais caché
                !
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Comment communiquer avec un hôte d'espace ?",
      answer: (
        <>
          <p className="mb-4">Plusieurs moyens de contacter votre hôte selon l'étape de votre réservation :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📝 Avant la réservation :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Questions générales :</strong> Bouton "Contacter l'hôte" sur la page de l'espace
                </li>
                <li>
                  <strong>Messagerie intégrée :</strong> Communication sécurisée via la plateforme
                </li>
                <li>
                  <strong>Réponse rapide :</strong> La plupart des hôtes répondent sous 24h
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Après confirmation :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Coordonnées complètes :</strong> Téléphone et email direct de l'hôte
                </li>
                <li>
                  <strong>WhatsApp/SMS :</strong> Contact direct pour les urgences
                </li>
                <li>
                  <strong>Chat en temps réel :</strong> Messagerie instantanée dans votre espace client
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📞 Le jour J :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Numéro direct :</strong> Accès aux coordonnées personnelles de l'hôte
                </li>
                <li>
                  <strong>Support d'urgence :</strong> Notre équipe joignable au 09 77 93 30 63
                </li>
                <li>
                  <strong>Instructions d'accès :</strong> Codes, parking, consignes spéciales
                </li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">💡 Bonnes pratiques :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Soyez précis dans vos questions</li>
                <li>Respectez les délais de réponse</li>
                <li>Confirmez votre arrivée 24h avant</li>
                <li>Gardez les numéros d'urgence à portée</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Quels sont les différents statuts de réservation et que signifient-ils ?",
      answer: (
        <>
          <p className="mb-4">Voici tous les statuts possibles de vos réservations sur EventToBee :</p>

          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">⏳ Statuts en attente :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>"En attente" :</strong> Demande envoyée, attente de réponse de l'hôte (max 48h)
                </li>
                <li>
                  <strong>"En cours de validation" :</strong> L'hôte examine votre demande
                </li>
                <li>
                  <strong>"Paiement en attente" :</strong> Confirmation de l'hôte reçue, finalisation du paiement
                  requise
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Statuts confirmés :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>"Confirmé" :</strong> Réservation acceptée et payée, prête pour votre événement
                </li>
                <li>
                  <strong>"En cours" :</strong> Votre événement est actuellement en cours
                </li>
                <li>
                  <strong>"Terminé" :</strong> Événement fini avec succès, possibilité de laisser un avis
                </li>
              </ul>
            </div>

            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-semibold mb-2">❌ Statuts d'annulation :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>"Refusé" :</strong> L'hôte a décliné votre demande, remboursement automatique
                </li>
                <li>
                  <strong>"Annulé par le client" :</strong> Vous avez annulé, remboursement selon politique
                </li>
                <li>
                  <strong>"Annulé par l'hôte" :</strong> L'hôte a annulé, remboursement intégral + compensation
                </li>
                <li>
                  <strong>"Expiré" :</strong> Pas de réponse dans les 48h, remboursement automatique
                </li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">⚠️ Statuts problématiques :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>"Litige" :</strong> Problème signalé, médiation en cours
                </li>
                <li>
                  <strong>"Remboursement en cours" :</strong> Traitement du remboursement (5-10 jours)
                </li>
                <li>
                  <strong>"No-show" :</strong> Non-présentation à l'événement confirmé
                </li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📱 Suivi en temps réel :</h4>
              <p className="text-sm">
                Tous les changements de statut déclenchent une notification instantanée par email et dans votre
                espace client.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Où puis-je voir le statut de ma réservation et de mes réservations passées ?",
      answer: (
        <>
          <p className="mb-4">Accédez facilement à toutes vos réservations depuis votre espace personnel :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">💻 Espace client web :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Connectez-vous à votre compte EventToBee</li>
                <li>Cliquez sur "Mes réservations" dans le menu principal</li>
                <li>Filtrez par statut : "En cours", "Confirmées", "Passées", "Annulées"</li>
                <li>Cliquez sur une réservation pour voir tous les détails</li>
              </ol>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">📱 Application mobile :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Tableau de bord :</strong> Vue d'ensemble de toutes vos réservations
                </li>
                <li>
                  <strong>Notifications push :</strong> Alertes en temps réel des changements
                </li>
                <li>
                  <strong>Accès hors ligne :</strong> Consultez vos réservations sans connexion
                </li>
                <li>
                  <strong>Géolocalisation :</strong> Itinéraire direct vers vos espaces réservés
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📊 Informations disponibles :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Détails complets :</strong> Date, heure, lieu, prix, durée
                </li>
                <li>
                  <strong>Statut en temps réel :</strong> Mise à jour automatique
                </li>
                <li>
                  <strong>Contact hôte :</strong> Coordonnées et messagerie directe
                </li>
                <li>
                  <strong>Factures :</strong> Téléchargement des reçus et factures
                </li>
                <li>
                  <strong>Historique :</strong> Toutes vos réservations depuis l'inscription
                </li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">🔔 Notifications automatiques :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Rappels :</strong> 24h et 2h avant votre événement
                </li>
                <li>
                  <strong>Changements :</strong> Toute modification de statut
                </li>
                <li>
                  <strong>Messages :</strong> Communications de l'hôte
                </li>
                <li>
                  <strong>Évaluations :</strong> Invitation à laisser un avis après l'événement
                </li>
              </ul>
            </div>

            <div className="p-4 bg-orange-50 rounded-lg">
              <h4 className="font-semibold mb-2">📧 Historique par email :</h4>
              <p className="text-sm">
                Tous les emails de confirmation sont conservés dans votre boîte mail avec les références de
                réservation pour un accès permanent.
              </p>
            </div>
          </div>
        </>
      ),
    },
  ],
  // hôte: [
  //   {
  //     question: "Comment se connecter ?",
  //     answer: (
  //       <>
  //         <span className="text-center">
  //           Bienvenue sur Event to Bee ! Pour profiter pleinement de nos services, suivez ces étapes simples pour
  //           créer votre compte ou bien se connecter
  //         </span>
  //         <ol className="ol">
  //           <li>
  //             <span>
  //               {" "}
  //               Création de compte Si vous êtes nouveau sur Event to bee, vous devrez créer un compte pour profiter
  //               pleinement de nos services. <b> Suivez ces étapes simples:</b>
  //             </span>
  //             <p style={{ textAlign: "justify", margin: "15px" }}>
  //               <ul className="c" style={{ listStyleType: "square" }}>
  //                 <li>
  //                   <b> Accédez à la page de connexion:&nbsp; </b> Cliquez sur le bouton "Compte" depuis la page
  //                   d'accueil pour accéder à la page de connexion.
  //                 </li>

  //                 <li>
  //                   <b> Choisissez votre type de compte: &nbsp; </b> Sélectionnez le type de compte qui correspond
  //                   à votre rôle sur la plateforme : Client, Prestataire, Propriétaire.
  //                 </li>

  //                 <li>
  //                   {" "}
  //                   <b> Remplissez les champs obligatoires: &nbsp; </b> Suivez les étapes indiquées et complétez
  //                   les informations nécessaires.
  //                 </li>
  //                 <li>
  //                   {" "}
  //                   <b> Cliquez sur "S'inscrire":&nbsp; </b> Une fois que vous avez rempli les champs, cliquez sur
  //                   le bouton "S'inscrire" pour créer votre compte.{" "}
  //                 </li>

  //                 <li>
  //                   {" "}
  //                   <b>Vérifiez votre adresse e-mail: &nbsp; </b> Consultez votre boîte de réception, où vous
  //                   trouverez un e-mail de confirmation. Cliquez sur le lien fourni pour activer votre compte.{" "}
  //                 </li>
  //               </ul>
  //             </p>
  //           </li>

  //           <li>
  //             <span style={{ justifyContent: "start" }}>
  //               {" "}
  //               Connexion à votre compte existant Si vous avez déjà un compte,
  //               <b> suivez ces étapes pour vous connecter :</b>{" "}
  //             </span>
  //             <ul className="c" style={{ listStyleType: "square" }}>
  //               <p style={{ textAlign: "start", margin: "15px" }}>
  //                 <li>
  //                   <b> Accédez à la page de connexion : &nbsp; </b>Cliquez sur le bouton "Compte" depuis la page
  //                   d'accueil pour accéder à la page de connexion.
  //                 </li>
  //                 <li>
  //                   <b> Saisissez vos identifiants : &nbsp; </b> Entrez votre adresse e-mail et votre mot de passe
  //                   dans les champs appropriés.
  //                 </li>
  //                 <li>
  //                   <b> Cliquez sur "Se connecter" : &nbsp; </b> Une fois vos informations saisies, cliquez sur le
  //                   bouton "Se connecter" pour accéder à votre compte Event to Bee.
  //                 </li>
  //                 <li>
  //                   {" "}
  //                   Si vous avez oublié votre mot de passe, cliquez sur "Mot de passe oublié" pour réinitialiser
  //                   votre mot de passe.
  //                 </li>
  //               </p>
  //             </ul>
  //           </li>
  //         </ol>
  //       </>
  //     ),
  //   },
  //   {
  //     question: "Comment créer un espace en tant que propriétaire ?",
  //     answer: (
  //       <>
  //         {" "}
  //         <ol>
  //           <li>
  //             <p style={{ textAlign: "justify", margin: "15px" }}>
  //               <ul className="c" style={{ listStyleType: "square" }}>
  //                 <li>
  //                   <b> Connectez-vous à votre compte: &nbsp; </b>Accédez à votre compte propriétaire sur Event to
  //                   Bee en utilisant vos identifiants.
  //                 </li>{" "}
  //                 <li>
  //                   <b> Accédez à la section "Mes espaces": &nbsp; </b>Une fois connecté, recherchez la section
  //                   dédiée à la gestion des espaces dans mon compte.
  //                 </li>
  //                 <li>
  //                   <b> Cliquez sur "Ajouter un nouvel espace" : &nbsp; </b> Sélectionnez l'option pour ajouter un
  //                   nouvel espace à votre liste.
  //                 </li>
  //                 <li>
  //                   <b> Remplissez les informations requises : &nbsp; </b> Complétez les détails nécessaires pour
  //                   décrire votre espace, y compris son nom, sa localisation, sa capacité, et tout autre
  //                   renseignement pertinent.
  //                 </li>
  //                 <li>
  //                   <b> Téléchargez des photos :&nbsp; </b> Ajoutez des images de qualité pour présenter votre
  //                   espace de manière attrayante. Les photos peuvent avoir un impact significatif sur la décision
  //                   des clients potentiels.
  //                 </li>
  //                 <li>
  //                   <b> Configurez les disponibilités : &nbsp; </b> Définissez les horaires de disponibilité de
  //                   votre espace pour que les clients puissent planifier leurs événements en fonction de vos
  //                   créneaux.
  //                 </li>
  //                 <li>
  //                   {" "}
  //                   <b>Fixez le tarif : &nbsp; </b> Indiquez le coût de la location de votre espace et précisez les
  //                   modalités de paiement.
  //                 </li>
  //                 <li>
  //                   {" "}
  //                   <b>Révisez les conditions d'utilisation : &nbsp; </b> Examinez et acceptez les conditions
  //                   d'utilisation de la plateforme pour la mise en location de votre espace.
  //                 </li>
  //                 <li>
  //                   {" "}
  //                   <b>Cliquez sur "Publier" : &nbsp; </b> Une fois toutes les informations correctement saisies,
  //                   cliquez sur le bouton approprié pour publier votre espace sur la plateforme.
  //                 </li>
  //               </ul>
  //             </p>
  //           </li>
  //         </ol>
  //       </>
  //     ),
  //   },
  //   {
  //     question: "Comment gérer mes reservation ?",
  //     answer: (
  //       <>
  //         <ol>
  //           <li>
  //             <p style={{ textAlign: "justify", margin: "15px" }}>
  //               <ul className="c" style={{ listStyleType: "square" }}>
  //                 <li>
  //                   <b> Connectez-vous à votre compte : &nbsp; </b>Accédez à votre compte propriétaire sur Event to
  //                   Bee en utilisant vos identifiants.
  //                 </li>{" "}
  //                 <li>
  //                   <b> Accédez à la section "Ma reservation" : &nbsp; </b>Une fois connecté, recherchez la section
  //                   dédiée à la gestion des réservations dans mon compte.
  //                 </li>
  //                 <li>
  //                   <b> Vue d'Ensemble : &nbsp; </b> Consultez une vue d'ensemble de toutes vos réservations
  //                   actuelles et passées Détails de la Réservation : Cliquez sur chaque réservation pour accéder
  //                   aux détails spécifiques tels que la date, l'heure, les clients et les spécifications de
  //                   l'événement.
  //                   <br />
  //                   En tant que <b> propriétaire</b> sur notre plateforme, vous avez la flexibilité de réserver
  //                   votre propre espace pour des dates spécifiques. Si vous souhaitez utiliser votre propre espace
  //                   à une date donnée
  //                 </li>
  //               </ul>
  //             </p>
  //           </li>
  //         </ol>
  //       </>
  //     ),
  //   },
  //   {
  //     question: "Comment gérer le paiement ?",
  //     answer: (
  //       <>
  //         <ol>
  //           <li>
  //             <p style={{ textAlign: "justify", margin: "15px" }}>
  //               <ul className="c" style={{ listStyleType: "square" }}>
  //                 <li>
  //                   <b> Vérification des Détails : &nbsp; </b>Notre équipe examine attentivement les détails de
  //                   l'événement pour garantir un déroulement sans problème, vérifiant qu'aucune réclamation ou
  //                   problème n'a été signalé par le client.
  //                 </li>{" "}
  //                 <li>
  //                   <b> Paiement au Propriétaire : &nbsp; </b>Lorsque tous les détails sont confirmés et que tout
  //                   est conforme, notre application effectue automatiquement le paiement au propriétaire de
  //                   l'espace. Ce processus de paiement sera initié dans un délai de 48 heures à compter de la date
  //                   de l'événement.
  //                 </li>
  //               </ul>
  //             </p>
  //           </li>
  //         </ol>
  //       </>
  //     ),
  //   },
  // ],

  // Prestataire
  prestataire: [
    {
      question: "Comment créer un compte prestataire ?",
      answer: (
        <>
          <p className="mb-4">Devenez prestataire sur EventToBee et développez votre activité :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📝 Étapes d'inscription :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>
                  <strong>Inscription :</strong> Cliquez sur "Devenir Prestataire" depuis la page d'accueil
                </li>
                <li>
                  <strong>Type de compte :</strong> Sélectionnez "Prestataire de services"
                </li>
                <li>
                  <strong>Informations personnelles :</strong> Nom, prénom, email, téléphone
                </li>
                <li>
                  <strong>Informations professionnelles :</strong> Nom de l'entreprise, SIRET, domaine d'activité
                </li>
                <li>
                  <strong>Vérification :</strong> Validation de votre email et documents
                </li>
              </ol>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">📋 Documents requis :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Pièce d'identité :</strong> Carte d'identité ou passeport
                </li>
                <li>
                  <strong>Justificatif d'entreprise :</strong> Extrait Kbis ou auto-entrepreneur
                </li>
                <li>
                  <strong>Assurance professionnelle :</strong> Responsabilité civile professionnelle
                </li>
                <li>
                  <strong>Références :</strong> Portfolio, témoignages clients (optionnel)
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">🎯 Domaines d'activité :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Traiteur et restauration</li>
                <li>Animation et divertissement</li>
                <li>Décoration et floristry</li>
                <li>Photographie et vidéographie</li>
                <li>Sonorisation et éclairage</li>
                <li>Service de sécurité</li>
                <li>Nettoyage et maintenance</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">✅ Validation du compte :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Délai :</strong> Vérification sous 48-72h ouvrées
                </li>
                <li>
                  <strong>Notification :</strong> Email de confirmation d'activation
                </li>
                <li>
                  <strong>Formation :</strong> Accès aux guides et tutoriels
                </li>
                <li>
                  <strong>Support dédié :</strong> Accompagnement personnalisé
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Comment ajouter une nouvelle prestation ?",
      answer: (
        <>
          <p className="mb-4">Créez vos offres de services pour attirer plus de clients :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">🚀 Création d'une prestation :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>
                  <strong>Accédez à "Mes prestations" :</strong> Depuis votre tableau de bord
                </li>
                <li>
                  <strong>Cliquez sur "Ajouter une prestation" :</strong> Bouton dans le menu principal
                </li>
                <li>
                  <strong>Choisissez votre catégorie :</strong> Sélectionnez le type de service
                </li>
                <li>
                  <strong>Remplissez les détails :</strong> Titre, description complète, équipements
                </li>
                <li>
                  <strong>Ajoutez des photos :</strong> Minimum 3 photos haute qualité
                </li>
                <li>
                  <strong>Définissez vos tarifs :</strong> Prix selon durée/forfait
                </li>
                <li>
                  <strong>Zone d'intervention :</strong> Délimitez vos zones de service
                </li>
                <li>
                  <strong>Publiez :</strong> Mise en ligne après validation
                </li>
              </ol>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">💰 Stratégie tarifaire :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Tarif horaire :</strong> Prix à l'heure pour services flexibles
                </li>
                <li>
                  <strong>Forfait journée :</strong> Prix fixe pour événements complets
                </li>
                <li>
                  <strong>Forfait sur mesure :</strong> Devis personnalisé selon demande
                </li>
                <li>
                  <strong>Options supplémentaires :</strong> Services complémentaires payants
                </li>
                <li>
                  <strong>Frais de déplacement :</strong> Coûts de transport selon distance
                </li>
              </ul>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📸 Optimisation visuelle :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Photos professionnelles :</strong> Images haute résolution et bien cadrées
                </li>
                <li>
                  <strong>Variété d'angles :</strong> Montrez tous les aspects de votre service
                </li>
                <li>
                  <strong>Réalisations passées :</strong> Mettez en avant vos meilleurs projets
                </li>
                <li>
                  <strong>Équipe en action :</strong> Photos de votre équipe au travail
                </li>
                <li>
                  <strong>Matériel :</strong> Présentez vos équipements professionnels
                </li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">🏆 Conseils pour réussir :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Description détaillée et attractive</li>
                <li>Mots-clés pertinents pour le référencement</li>
                <li>Tarifs compétitifs et transparents</li>
                <li>Réactivité aux demandes de devis</li>
                <li>Portfolio riche et varié</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
    {
      question: "Comment gérer les demandes de devis ?",
      answer: (
        <>
          <p className="mb-4">Optimisez votre taux de conversion en gérant efficacement vos devis :</p>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2">📬 Réception des demandes :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Notifications immédiates :</strong> Email et SMS dès réception
                </li>
                <li>
                  <strong>Détails complets :</strong> Type d'événement, date, lieu, budget
                </li>
                <li>
                  <strong>Coordonnées client :</strong> Contact direct disponible
                </li>
                <li>
                  <strong>Délai de réponse :</strong> 24h maximum recommandé
                </li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold mb-2">💼 Traitement des devis :</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>
                  <strong>Analysez la demande :</strong> Vérifiez la faisabilité et vos disponibilités
                </li>
                <li>
                  <strong>Préparez votre proposition :</strong> Devis détaillé avec tous les éléments
                </li>
                <li>
                  <strong>Personnalisez l'offre :</strong> Adaptez selon les besoins spécifiques
                </li>
                <li>
                  <strong>Envoyez rapidement :</strong> Via la plateforme ou email direct
                </li>
                <li>
                  <strong>Suivez la réponse :</strong> Relancez poliment si nécessaire
                </li>
              </ol>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">📋 Contenu du devis :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Description détaillée :</strong> Tous les services inclus
                </li>
                <li>
                  <strong>Tarification claire :</strong> Prix HT, TVA, total TTC
                </li>
                <li>
                  <strong>Durée d'intervention :</strong> Horaires précis d'installation et démontage
                </li>
                <li>
                  <strong>Matériel fourni :</strong> Liste complète des équipements
                </li>
                <li>
                  <strong>Conditions :</strong> Modalités de paiement, annulation, force majeure
                </li>
                <li>
                  <strong>Validité :</strong> Durée de validité du devis (recommandé : 30 jours)
                </li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold mb-2">📊 Suivi et optimisation :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Tableau de bord :</strong> Vue d'ensemble de tous vos devis
                </li>
                <li>
                  <strong>Statuts :</strong> En attente, accepté, refusé, expiré
                </li>
                <li>
                  <strong>Taux de conversion :</strong> Statistiques de performance
                </li>
                <li>
                  <strong>Feedback client :</strong> Raisons de refus pour amélioration
                </li>
                <li>
                  <strong>Relances automatiques :</strong> Rappels pour devis en attente
                </li>
              </ul>
            </div>

            <div className="p-4 bg-orange-50 rounded-lg">
              <h4 className="font-semibold mb-2">🎯 Bonnes pratiques :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Répondez dans les 2h pour les demandes urgentes</li>
                <li>Posez des questions précises pour affiner l'offre</li>
                <li>Proposez des alternatives ou options</li>
                <li>Incluez des témoignages clients dans vos devis</li>
                <li>Restez disponible pour répondre aux questions</li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ],
  prestat: [
    {
      question: "Comment se connecter ?",
      answer: (
        <>
          <span className="text-center">
            Bienvenue sur Event to Bee ! Pour profiter pleinement de nos services, suivez ces étapes simples pour
            créer votre compte ou bien se connecter
          </span>
          <ol className="ol">
            <li>
              <span>
                {" "}
                Création de compte Si vous êtes nouveau sur Event to bee, vous devrez créer un compte pour profiter
                pleinement de nos services. <b> Suivez ces étapes simples:</b>
              </span>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b> Accédez à la page de connexion:&nbsp; </b> Cliquez sur le bouton "Compte" depuis la page
                    d'accueil pour accéder à la page de connexion.
                  </li>

                  <li>
                    <b> Choisissez votre type de compte: &nbsp; </b> Sélectionnez le type de compte qui correspond
                    à votre rôle sur la plateforme : Client, Prestataire, Propriétaire.
                  </li>

                  <li>
                    {" "}
                    <b> Remplissez les champs obligatoires: &nbsp; </b> Suivez les étapes indiquées et complétez
                    les informations nécessaires.
                  </li>
                  <li>
                    {" "}
                    <b> Cliquez sur "S'inscrire":&nbsp; </b> Une fois que vous avez rempli les champs, cliquez sur
                    le bouton "S'inscrire" pour créer votre compte.{" "}
                  </li>

                  <li>
                    {" "}
                    <b>Vérifiez votre adresse e-mail: &nbsp; </b> Consultez votre boîte de réception, où vous
                    trouverez un e-mail de confirmation. Cliquez sur le lien fourni pour activer votre compte.{" "}
                  </li>
                </ul>
              </p>
            </li>

            <li>
              <span style={{ justifyContent: "start" }}>
                {" "}
                Connexion à votre compte existant Si vous avez déjà un compte,
                <b> suivez ces étapes pour vous connecter :</b>{" "}
              </span>
              <ul className="c" style={{ listStyleType: "square" }}>
                <p style={{ textAlign: "start", margin: "15px" }}>
                  <li>
                    <b> Accédez à la page de connexion : &nbsp; </b>Cliquez sur le bouton "Compte" depuis la page
                    d'accueil pour accéder à la page de connexion.
                  </li>
                  <li>
                    <b> Saisissez vos identifiants : &nbsp; </b> Entrez votre adresse e-mail et votre mot de passe
                    dans les champs appropriés.
                  </li>
                  <li>
                    <b> Cliquez sur "Se connecter" : &nbsp; </b> Une fois vos informations saisies, cliquez sur le
                    bouton "Se connecter" pour accéder à votre compte Event to Bee.
                  </li>
                  <li>
                    {" "}
                    Si vous avez oublié votre mot de passe, cliquez sur "Mot de passe oublié" pour réinitialiser
                    votre mot de passe.
                  </li>
                </p>
              </ul>
            </li>
          </ol>
        </>
      ),
    },
    {
      question: "Comment ajouter une prestation en tant que prestataire ? ",
      answer: (
        <>
          <ol>
            <li>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b> Connectez-vous à votre compte: &nbsp; </b>
                    Accédez à votre compte propriétaire sur Event to Bee en utilisant vos identifiants.
                  </li>
                  <li>
                    <b> Accédez à la section "Mes prestations" : &nbsp; </b>Une fois connecté, recherchez la
                    section dédiée à la gestion des prestations dans mon compte.
                  </li>{" "}
                  <li>
                    <b> Cliquez sur "Ajouter une nouvelle prestation" : &nbsp; </b>
                    Sélectionnez l'option pour ajouter une nouvelle prestation.
                  </li>
                  <li>
                    <b> Remplissez les informations requises : &nbsp; </b>
                    Complétez les détails nécessaires pour décrire votre prestation, tels que le nom, la
                    description, le type d'événements, les équipements supplémentaires, les tarifs et toute
                    information pertinente.
                  </li>
                  <li>
                    <b> Téléchargez des photos : &nbsp; </b>
                    Ajoutez des détails visuels pour rendre votre prestation attrayante. Incluez des images de
                    qualité pour illustrer vos services.
                  </li>
                  <li>
                    <b> Paramètres de disponibilité : &nbsp; </b>
                    Spécifiez les horaires disponibles pour votre prestation. Cela garantira une coordination
                    fluide avec les clients.
                  </li>{" "}
                  <li>
                    <b> Fixez le tarif: &nbsp; </b>
                    Définissez clairement vos tarifs. Si vous proposez des options supplémentaires comme des
                    forfaits ou des services complémentaires, spécifiez-les ici.
                  </li>{" "}
                  <li>
                    <b> Vérification des informations: &nbsp; </b>Avant de publier, assurez-vous de vérifier toutes
                    les informations que vous avez fournies. Des détails précis attirent davantage les clients.
                  </li>
                  <li>
                    <b> Cliquez sur "Publier" : &nbsp; </b>Une fois toutes les informations correctement saisies,
                    cliquez sur le bouton approprié pour mettre en ligne votre prestation sur notre plateforme.
                  </li>
                </ul>
              </p>
            </li>
          </ol>
        </>
      ),
    },
    {
      question: "Comment gérer les demandes client ?",
      answer: (
        <>
          <ol>
            <li>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b> Connectez-vous à votre compte : &nbsp; </b>
                    Accédez à votre compte propriétaire sur Event to Bee en utilisant vos identifiants.{" "}
                  </li>
                  <li>
                    <b> Accédez à la section "Mes demande de devis " : &nbsp; </b>
                    Une fois connecté, recherchez la section dédiée à la gestion des devis dans mon compte.
                  </li>
                  <li>
                    <b> Consultez vos Demandes de Devis : &nbsp; </b>
                    Explorez les demandes en attente, en cours et complétées pour une gestion efficace.
                  </li>
                  <li>
                    <b> Réception des Demandes de Devis : &nbsp; </b>
                    Lorsqu'un client exprime le besoin d'un devis, veillez à ce qu'il fournisse toutes les
                    informations nécessaires pour établir une proposition précise.
                  </li>{" "}
                  <li>
                    <b> Accès aux coordonnées : &nbsp; </b>
                    Assurez-vous que toutes vos coordonnées sont à jour et disponibles. Les clients peuvent vous
                    contacter via <b> email, téléphone mobile et site web.</b>
                  </li>
                </ul>
              </p>
            </li>
          </ol>
        </>
      ),
    },
  ],

  // client
  client: [
    {
      question: "Comment se connecter ?",
      answer: (
        <>
          <span className="text-center">
            Bienvenue sur Event to Bee ! Pour profiter pleinement de nos services, suivez ces étapes simples pour
            créer votre compte ou bien se connecter
          </span>
          <ol className="ol">
            <li>
              <span>
                {" "}
                Création de compte Si vous êtes nouveau sur Event to bee, vous devrez créer un compte pour profiter
                pleinement de nos services. <b> Suivez ces étapes simples:</b>
              </span>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b> Accédez à la page de connexion:&nbsp; </b> Cliquez sur le bouton "Compte" depuis la page
                    d'accueil pour accéder à la page de connexion.
                  </li>

                  <li>
                    <b> Choisissez votre type de compte: &nbsp; </b> Sélectionnez le type de compte qui correspond
                    à votre rôle sur la plateforme : Client, Prestataire, Propriétaire.
                  </li>

                  <li>
                    {" "}
                    <b> Remplissez les champs obligatoires: &nbsp; </b> Suivez les étapes indiquées et complétez
                    les informations nécessaires.
                  </li>
                  <li>
                    {" "}
                    <b> Cliquez sur "S'inscrire":&nbsp; </b> Une fois que vous avez rempli les champs, cliquez sur
                    le bouton "S'inscrire" pour créer votre compte.{" "}
                  </li>

                  <li>
                    {" "}
                    <b>Vérifiez votre adresse e-mail: &nbsp; </b> Consultez votre boîte de réception, où vous
                    trouverez un e-mail de confirmation. Cliquez sur le lien fourni pour activer votre compte.{" "}
                  </li>
                </ul>
              </p>
            </li>

            <li>
              <span style={{ justifyContent: "start" }}>
                {" "}
                Connexion à votre compte existant Si vous avez déjà un compte,
                <b> suivez ces étapes pour vous connecter :</b>{" "}
              </span>
              <ul className="c" style={{ listStyleType: "square" }}>
                <p style={{ textAlign: "start", margin: "15px" }}>
                  <li>
                    <b> Accédez à la page de connexion : &nbsp; </b>Cliquez sur le bouton "Compte" depuis la page
                    d'accueil pour accéder à la page de connexion.
                  </li>
                  <li>
                    <b> Saisissez vos identifiants : &nbsp; </b> Entrez votre adresse e-mail et votre mot de passe
                    dans les champs appropriés.
                  </li>
                  <li>
                    <b> Cliquez sur "Se connecter" : &nbsp; </b> Une fois vos informations saisies, cliquez sur le
                    bouton "Se connecter" pour accéder à votre compte Event to Bee.
                  </li>
                  <li>
                    {" "}
                    Si vous avez oublié votre mot de passe, cliquez sur "Mot de passe oublié" pour réinitialiser
                    votre mot de passe.
                  </li>
                </p>
              </ul>
            </li>
          </ol>
        </>
      ),
    },
    {
      question: "Comment reserver un espace ?",
      answer: (
        <>
          <span className="text-center">
            Event to Bee rend la réservation d'espaces simple et intuitive. Suivez ces étapes pour organiser votre
            événement sans tracas :
          </span>{" "}
          <ol>
            <li>
              <span>
                <b> Connectez-vous à votre compte: &nbsp;</b> Avant de commencer, assurez-vous d'être connecté à
                votre compte Event to Bee. Si vous n'avez pas encore de compte, suivez les étapes pour créer un
                compte, comme indiqué dans notre guide "Création de compte et Connexion".
              </span>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b> Consultez notre page "Blog": &nbsp; </b>
                    Pour enrichir votre expérience et en apprendre davantage sur Event to Bee. Des astuces sur les
                    fonctionnalités avancées à l'inspiration pour la décoration de vos espaces et événements,
                    obtenez des conseils pour organiser des événements mémorables et découvrez les dernières
                    tendances et idées créatives. Le blog est conçu pour vous offrir une source complète
                    d'informations et d'inspiration afin de tirer le meilleur parti de votre expérience sur notre
                    plateforme.
                  </li>

                  <li>
                    <b> Recherchez l'espace idéal : &nbsp; </b> Explorez notre large gamme d'espaces disponibles en
                    utilisant les filtres tels que la région et l'evenement .
                  </li>

                  <li>
                    <b> Consultez les détails de l'espace : &nbsp; </b> Cliquez sur un espace qui correspond à vos
                    besoins pour obtenir des informations détaillées. Vérifiez la capacité, les équipements inclus,
                    les tarifs, et assurez-vous que l'espace répond à vos exigences.
                  </li>

                  <li>
                    <b> Partagez avec vos amis :&nbsp; </b> Partagez l'espace que vous avez sélectionné avec vos
                    amis en utilisant les options de partage disponibles sur la page de l'espace.
                  </li>

                  <li>
                    <b> Ajoutez à vos favoris : &nbsp; </b> Marquez l'espace en tant que favori pour le retrouver
                    facilement ultérieurement. Vous pouvez gérer vos favoris dans la section "favoris espaces"
                    dédiée à votre compte Event to Bee.
                  </li>

                  <li>
                    {" "}
                    <b>Vérifiez la disponibilité : &nbsp; </b> Sur la page de l'espace, consultez le calendrier de
                    disponibilité pour trouver une date et une heure qui vous conviennent. Certains espaces peuvent
                    avoir des créneaux horaires spécifiques disponibles.
                  </li>

                  <li>
                    {" "}
                    <b>Sélectionnez vos options : &nbsp; </b> Choisissez les options supplémentaires, le cas
                    échéant, comme la configuration de l'espace, les services supplémentaires, etc.
                  </li>
                  <li>
                    {" "}
                    <b>Demande de réservation : &nbsp; </b> Cliquez sur le bouton "Demande de réservation" . Vous
                    serez redirigé vers votre panier où vous pouvez passer en revue votre sélection.
                  </li>

                  <li>
                    {" "}
                    <b>Procédez au paiement : &nbsp; </b> Une fois que vous êtes prêt, cliquez sur "Payer" pour
                    passer à la page de paiement. Suivez les instructions pour finaliser la réservation en
                    fournissant les informations nécessaires.
                  </li>

                  <li>
                    {" "}
                    <b>Confirmation de réservation : &nbsp; </b>Une fois le paiement effectué, Vous pourrez
                    également vérifier vos réservations dans la section "Mes reservation" de votre compte Event to
                    Bee.
                  </li>
                </ul>
              </p>
            </li>
          </ol>
        </>
      ),
    },
    {
      question: "Politique d'annulation et de remboursement",
      answer: (
        <>
          <span>
            Nous comprenons que parfois, des imprévus surviennent. Afin de vous offrir une politique d'annulation
            équitable, veuillez prendre connaissance des détails suivants :
          </span>
          <ol>
            <li>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b> Remboursement de 70 % de la valeur de la réservation : &nbsp; </b>
                    En cas d'annulation effectuée avant 5 jours de l'heure prévue d'arrivée, les clients peuvent
                    bénéficier d'un remboursement de 70 % de la valeur total de la réservation.
                  </li>

                  <li>
                    <b> Conditions d'annulation : &nbsp; </b>
                    Les clients doivent annuler leur réservation au minimum 5 jours avant l'heure prévue d'arrivée
                    pour être éligibles au remboursement. Après cette période, les annulations ne seront plus
                    acceptées, et aucun remboursement ne sera accordé.
                  </li>
                </ul>
              </p>
            </li>
          </ol>
        </>
      ),
    },
    {
      question: "Dépôt de garantie (cautionnement) ",
      answer: (
        <>
          <ol>
            <li>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b> Montant du dépôt : &nbsp; </b>
                    Un dépôt de garantie variable, spécifique à chaque espace, sera remis le jour de votre arrivée
                    pour assurer la protection de l'espace réservé.
                  </li>

                  <li>
                    <b> Remboursement du dépôt : &nbsp; </b>
                    Le remboursement du dépôt de garantie, s'il a été prélevé, sera effectué à la conclusion de
                    votre événement, sous réserve qu'aucun dommage ou violation des termes et conditions ne soit
                    constaté.
                  </li>
                </ul>
              </p>
            </li>
          </ol>
        </>
      ),
    },
    {
      question: "Comment procéder à une annulation et au remboursement ? ",
      answer: (
        <>
          <ol>
            <li>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b>Connectez-vous à votre compte : &nbsp; </b>
                    Accédez à votre compte sur Event to Bee en utilisant vos identifiants.
                  </li>

                  <li>
                    <b> Accédez à vos réservations : &nbsp; </b>
                    Trouvez la réservation que vous souhaitez annuler dans la section "Mes réservations".
                  </li>

                  <li>
                    <b> Sélectionnez "Annuler la réservation" : &nbsp; </b>
                    Choisissez l'option d'annulation et suivez les instructions pour confirmer l'annulation.
                  </li>

                  <li>
                    <b> Confirmation par email : &nbsp; </b>
                    Un email de confirmation sera envoyé au client pour valider l'annulation. Une fois que le
                    client aura confirmé l'annulation par email, la réservation sera officiellement annulée.{" "}
                  </li>

                  <li>
                    <b> Traitement du remboursement : &nbsp; </b>
                    Une fois l'annulation effectuée dans les délais requis, le remboursement de 70 % sera traité.
                  </li>
                </ul>
              </p>
            </li>
          </ol>
        </>
      ),
    },
    {
      question: "Comment ajouter un avis ou un commentaire en tant que client ?",
      answer: (
        <>
          <ol>
            <li>
              <p style={{ textAlign: "justify", margin: "15px" }}>
                <ul className="c" style={{ listStyleType: "square" }}>
                  <li>
                    <b>Connectez-vous à votre compte : &nbsp; </b>
                    Accédez à votre compte sur Event to Bee en utilisant vos identifiants.
                  </li>

                  <li>
                    <b> Trouvez un espace ou une prestation : &nbsp; </b>
                    Accédez à la section "Espaces" ou "Prestations" pour rechercher et sélectionner l'espace ou la
                    prestation que vous souhaitez évaluer.
                  </li>

                  <li>
                    <b> Accédez à la section des avis : &nbsp; </b>
                    Une fois sur la page de l'espace ou de la prestation que vous souhaitez évaluer, recherchez la
                    section dédiée aux avis et commentaires.
                  </li>

                  <li>
                    <b> Écrivez votre avis : &nbsp; </b>
                    Partagez votre expérience en détaillant ce que vous avez apprécié et toute suggestion
                    d'amélioration. Soyez honnête et constructif.
                  </li>

                  <li>
                    <b> Attribuez une note : &nbsp; </b>
                    Selon les options disponibles, donnez une note ou une évaluation à l'espace ou à la prestation,
                    généralement sur une échelle de 1 à 5 étoiles.
                  </li>

                  <li>
                    <b> Soumettez votre avis : &nbsp; </b>
                    Une fois votre commentaire terminé et votre évaluation attribuée, cliquez sur "Soumettre" pour
                    partager votre avis avec la communauté.
                  </li>
                </ul>
              </p>
            </li>
          </ol>
        </>
      ),
    },
  ],
};
